import { createSelector } from "reselect";

import type {
  Reconciliation,
  ReconciliationTransaction,
} from "src/interfaces/reconciliation.interface";
import type { Sale } from "src/interfaces/sale.interface";

import { transactionsSelector } from "src/store/transaction/selector";
import { getAllSales } from "src/store/sale/selector";

import { bankAccountByIdSelector } from "./core";

export * from "./core";

/** Matches backend bank-reconciliations getSaleReconciliationDate. */
function getReconciliationSaleDate(sale: Sale): Date | undefined {
  const isReturn = sale.is_return || sale.original_id;
  if (isReturn) return sale.return_date;
  return sale.posted_date ?? sale.sale_date;
}

function getReconciliationDisplayDate(sale: Sale): Date | undefined {
  const isReturn = sale.is_return || sale.original_id;
  if (isReturn) return sale.return_date;
  return sale.sale_date;
}

const bankAccountSalesFilterSelector = createSelector(
  [
    getAllSales,
    (state, { id }: { id: string }) => bankAccountByIdSelector(state, id),
    (_, { start }: { start: Date | undefined }) => start,
    (_, { end }: { end: Date | undefined }) => end,
  ],
  (sales, bankAccount, start, end) => {
    if (!bankAccount) return [];
    return sales.filter((sale) => {
      if (start || end) {
        const reconciliationDate = getReconciliationSaleDate(sale);
        if (!reconciliationDate) return false;
        const time = reconciliationDate.getTime();
        if (start && time <= start.getTime()) return false;
        if (end && time > end.getTime()) return false;
      }
      if (sale.sale_platform_account_id) {
        if (sale.sale_platform_account_id !== bankAccount.id) return false;
      } else {
        if (sale.sale_platform !== bankAccount.name) return false;
      }
      return true;
    });
  }
);

const bankAccountTransactionsFilterSelector = createSelector(
  [
    transactionsSelector,
    (state, { id }: { id: string }) => bankAccountByIdSelector(state, id),
    (_, { start }: { start: Date | undefined }) => start,
    (_, { end }: { end: Date | undefined }) => end,
  ],
  (transactions, bankAccount, start, end) => {
    if (!bankAccount || !transactions) return [];
    return transactions.filter((transaction) => {
      if (start || end) {
        if (!transaction.date) return false;
        const time = transaction.date.getTime();
        if (start && time <= start.getTime()) return false;
        if (end && time > end.getTime()) return false;
      }
      if (transaction.bank_account_id) {
        if (transaction.bank_account_id !== bankAccount.id) return false;
      } else {
        if (transaction.integration_name !== bankAccount.name) return false;
      }
      return true;
    });
  }
);

export interface BankAccountTransactionsAndBalanceSelectorArguments {
  id: string;
  start?: Date;
  end?: Date;
  transactions?: Reconciliation["transactions"];
}

export const bankAccountTransactionsAndBalanceSelector = createSelector(
  [
    (
      state,
      { id, start, end }: BankAccountTransactionsAndBalanceSelectorArguments
    ) => bankAccountSalesFilterSelector(state, { id, start, end }),
    (
      state,
      { id, start, end }: BankAccountTransactionsAndBalanceSelectorArguments
    ) =>
      bankAccountTransactionsFilterSelector(state, {
        id,
        start,
        end,
      }),
    (
      state,
      { transactions }: BankAccountTransactionsAndBalanceSelectorArguments
    ) => transactions,
  ],
  (sales, transactions, matchedTransactions) => {
    let balance = 0;
    let matchedBalance = 0;
    const rows: ReconciliationTransaction[] = [];

    for (const transaction of transactions) {
      if (!transaction.date) continue;
      const matched = !!matchedTransactions?.find?.(
        (t) => t.type === "transaction" && t.id === transaction.id
      );
      rows.push({
        date: transaction.date,
        id: `transaction-${transaction.id}`,
        collection: "transaction",
        collection_id: transaction.id,
        description: transaction.description,
        charge_description: transaction.name || "",
        matched,
        amount: -transaction.amount,
      });
      balance -= transaction.amount;
      if (matched || !matchedTransactions) matchedBalance -= transaction.amount;
    }

    for (const sale of sales) {
      const reconciliationDate = getReconciliationSaleDate(sale);
      if (!reconciliationDate) continue;
      const displayDate =
        getReconciliationDisplayDate(sale) ?? reconciliationDate;
      const isReturn = sale.is_return || sale.original_id;
      const matched = !!matchedTransactions?.find?.(
        (t) => t.type === "sale" && t.id === sale.id
      );

      let amount =
        sale.sale_price -
        (sale.transaction_fees || 0) -
        (sale.extra_transaction_fees || 0) -
        (sale.shipping_cost || 0) -
        (sale.extra_shipping_cost || 0);
      if (sale.liable_to_pay) amount += sale.sales_tax || 0;
      balance += amount;

      if (matched || !matchedTransactions) {
        matchedBalance += amount;
      }

      if (sale.sale_price) {
        rows.push({
          date: displayDate,
          id: `sale-price-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          description: sale.item_title,
          amount: sale.sale_price,
          charge_description: "",
          matched,
        });
      }

      if (sale.shipping_cost) {
        rows.push({
          date: displayDate,
          id: `shipping-cost-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          description: sale.item_title,
          amount: -sale.shipping_cost,
          charge_description: "",
          matched,
        });
      }

      if (sale.transaction_fees) {
        rows.push({
          date: displayDate,
          id: `transaction-fees-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          description: sale.item_title,
          amount: -sale.transaction_fees,
          charge_description: "",
          matched,
        });
      }

      if (sale.sales_tax && sale.liable_to_pay) {
        rows.push({
          date: displayDate,
          id: `sale-tax-payable-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          description: sale.item_title,
          amount: sale.sales_tax,
          charge_description: "",
          matched,
        });
      }

      if (isReturn) {
        if (sale.extra_shipping_cost) {
          rows.push({
            date: displayDate,
            id: `extra-shipping-cost-${sale.id}`,
            collection: "sale",
            collection_id: sale.id,
            description: sale.item_title,
            amount: -sale.extra_shipping_cost,
            charge_description: "",
            matched,
          });
        }

        if (sale.extra_transaction_fees) {
          rows.push({
            date: displayDate,
            id: `extra-transaction-fees-${sale.id}`,
            collection: "sale",
            collection_id: sale.id,
            description: sale.item_title,
            amount: -sale.extra_transaction_fees,
            charge_description: "",
            matched,
          });
        }
      }
    }

    if (Math.abs(balance || 0).toFixed(2) === "0.00") balance = 0;
    if (Math.abs(matchedBalance || 0).toFixed(2) === "0.00") matchedBalance = 0;

    rows.sort((a, b) => a.date.getTime() - b.date.getTime());

    return { transactions: rows, balance, matchedBalance };
  }
);
